import React, { ReactNode, useState } from "react";
import { useForm } from "@inertiajs/react";
import { session_path, new_registration_path } from "@/routes";
import { router } from "@inertiajs/react";
import { Box, Flex, Heading, VStack, Input, Button, Text, HStack } from "@chakra-ui/react";
import { Field } from "@/components/ui/field";
import DefaultLayout from "@/components/layouts/DefaultLayout";
import GoogleButton from "@/components/GoogleButton";
import loginBackground from "@/assets/login-background.png";
import loginBackgroundAvif from "@/assets/login-background.avif";
import { useAnalytics } from "@/hooks/useAnalytics";
import { SEOHead } from "@/components/SEOHead";

interface NewProps {
  errors?: { email?: string[] };
  email?: string;
}

const New = ({ errors = {}, email = "" }: NewProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { data, setData, post, processing } = useForm({
    email: email,
    password: "",
  });

  const TRACK = useAnalytics();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(session_path(), {
      onSuccess: () => {
        TRACK.USER_SIGNED_IN("email");
      },
    });
  };

  return (
    <>
      <SEOHead
        title="Sign In - Muro"
        description="Sign in to Muro to access your automated focus time blocking dashboard. Manage your deep work sessions and calendar integrations."
        canonical="/sign_in"
        noindex={true}
      />
      <Flex minH="100vh" w="full" overflow="hidden">
        <Box
          display={{ base: "none", lg: "flex" }}
          w={{ lg: "50%" }}
          position="relative"
          overflow="hidden"
          bg="teal.800"
        >
          <picture style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            <source srcSet={loginBackgroundAvif} type="image/avif" />
            <img
              src={loginBackground}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                opacity: imageLoaded ? 1 : 0,
                transition: "opacity 700ms ease-in",
              }}
              fetchPriority="high"
              decoding="async"
              onLoad={() => setImageLoaded(true)}
            />
          </picture>
        </Box>

        <Flex
          w={{ base: "full", lg: "50%" }}
          minH="100vh"
          alignItems="center"
          justifyContent="center"
          bg={{ base: "gray.50", lg: "white" }}
          px={{ base: 6, md: 12 }}
        >
          <Box maxW="md" w="full">
            <VStack gap={8} alignItems="stretch">
              <Heading
                as="h1"
                fontSize={{ base: "3xl", md: "4xl" }}
                fontWeight="semibold"
                color="teal.700"
                textAlign="left"
              >
                Welcome back!
              </Heading>

              <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                <VStack gap={6} w="full" alignItems="stretch">
                  {/* Email Field */}
                  <Box w="full">
                    <Field label="Email" htmlFor="email">
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder=""
                        value={data.email}
                        onChange={(e) => setData("email", e.target.value)}
                        bg="gray.100"
                        border="none"
                        _focus={{ bg: "white", borderColor: "teal.500", borderWidth: "1px" }}
                        h="12"
                      />
                    </Field>
                    {errors.email && (
                      <Text mt={2} fontSize="sm" color="red.600">
                        {errors.email[0]}
                      </Text>
                    )}
                  </Box>

                  <Box w="full">
                    <Field label="Password" htmlFor="password">
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        required
                        placeholder=""
                        value={data.password}
                        onChange={(e) => setData("password", e.target.value)}
                        bg="gray.100"
                        border="none"
                        _focus={{ bg: "white", borderColor: "teal.500", borderWidth: "1px" }}
                        h="12"
                      />
                    </Field>
                  </Box>

                  <Button
                    type="submit"
                    disabled={processing}
                    w="full"
                    bg="teal.600"
                    color="white"
                    _hover={{ bg: "teal.700" }}
                    h="12"
                    fontSize="md"
                    fontWeight="normal"
                    mt={2}
                  >
                    {processing ? "Signing in..." : "Login"}
                  </Button>

                  <HStack w="full" my={2}>
                    <Box flex="1" h="1px" bg="gray.300" />
                    <Text fontSize="sm" color="gray.600" px={3}>
                      OR
                    </Text>
                    <Box flex="1" h="1px" bg="gray.300" />
                  </HStack>

                  <GoogleButton text="Sign in with Google" action="sign_in" />

                  <Box textAlign="center" w="full" mt={4}>
                    <Button
                      variant="plain"
                      type="button"
                      onClick={() => router.visit(new_registration_path())}
                      color="gray.600"
                      _hover={{ color: "gray.800" }}
                      fontSize="sm"
                    >
                      Don&apos;t have an account? Sign up
                    </Button>
                  </Box>
                </VStack>
              </form>

              <Box mt={8} pt={8} borderTop="1px solid" borderColor="gray.200">
                <Text fontSize="sm" color="teal.700" fontStyle="italic" textAlign="center">
                  &ldquo;Believe in yourself is an overrated advice. Generate evidence&rdquo;
                </Text>
                <Text fontSize="sm" color="teal.600" textAlign="center" mt={1}>
                  - Ryan Holiday
                </Text>
              </Box>
            </VStack>
          </Box>
        </Flex>
      </Flex>
    </>
  );
};

New.layout = (page: ReactNode) => <DefaultLayout>{page}</DefaultLayout>;

export default New;
